import React, { useEffect, useMemo, useRef, useState } from "react";
import { Sparkles, RefreshCw, Copy, Check, AlertTriangle, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import SendToClientModal from "./SendToClientModal";

// Iter AE — Consolidated AI Recommendations bundle.
// Iter AO — Auto-generate on entry when the verbal debrief is present so the
// therapist isn't asked twice. Fallback "Generate recommendations" button only
// appears if auto-gen fails OR no recommendations have been produced yet.

function SectionCard({ title, testid, children }) {
  return (
    <div className="bg-white border border-paper-200 rounded-3xl p-5 sm:p-6" data-testid={testid}>
      <div className="label-overline mb-3 text-sage-700">{title}</div>
      {children}
    </div>
  );
}

function CopyButton({ text, testid }) {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text || "");
      setCopied(true);
      toast.success("Copied");
      setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copy failed"); }
  };
  return (
    <button
      type="button"
      onClick={onCopy}
      data-testid={testid}
      className="inline-flex items-center gap-1.5 text-xs text-ink-700 hover:text-ink-900 px-2.5 py-1 rounded-full hover:bg-paper-100"
    >
      {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

export default function RecommendationsBundleCard({
  sessionId,
  recommendations,
  outcome,
  onGenerated,
  hasFindings,
  client,
  autoTrigger = false,
}) {
  const [loading, setLoading] = useState(false);
  const [slowLoading, setSlowLoading] = useState(false);
  const [autoFailed, setAutoFailed] = useState(false);
  const [justRevealed, setJustRevealed] = useState(false);
  const autoFiredRef = useRef(false);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const r = recommendations || null;

  // Detect stale: snapshot.pain_after/etc differs from current outcome
  const stale = useMemo(() => {
    if (!r || !r.snapshot) return false;
    const snap = r.snapshot;
    if ((snap.pain_after ?? null) !== (outcome?.pain_after ?? null)) return true;
    if ((snap.pain_before ?? null) !== (outcome?.pain_before ?? null)) return true;
    const curRom = (outcome?.rom_measurements || []).length;
    if ((snap.rom_count ?? 0) !== curRom) return true;
    return false;
  }, [r, outcome]);

  const generate = async (force = false, { silent = false } = {}) => {
    if (!sessionId) {
      if (!silent) toast.error("Save the session draft first.");
      return false;
    }
    // Iter AP — minimum visibility window for the loading state. Even when the
    // server replies in 80 ms we keep the skeleton on screen long enough that
    // the transition reads as intentional rather than a flash-of-empty-state.
    const MIN_VISIBLE_MS = 350;
    const startedAt = Date.now();
    setLoading(true);
    setSlowLoading(false);
    let success = true;
    let returnedBundle = null;
    try {
      const { data } = await api.post(`/sessions/${sessionId}/recommendations/generate`, { force_regenerate: force });
      returnedBundle = data;
      setAutoFailed(false);
      if (!silent) toast.success(force ? "Recommendations refreshed" : "Recommendations ready");
    } catch (e) {
      success = false;
      const detail = e?.response?.data?.detail || "Generation failed";
      if (!silent) {
        toast.error(detail === "provide_findings_or_worked_areas"
          ? "Add a verbal debrief (or Clinical Findings) first."
          : `Could not generate: ${detail}`);
      }
      setAutoFailed(true);
    }
    const elapsed = Date.now() - startedAt;
    const finalize = () => {
      if (success && returnedBundle) {
        onGenerated && onGenerated(returnedBundle);
        // One render of opacity-0 → opacity-100 produces the fade-in.
        setJustRevealed(true);
        requestAnimationFrame(() => setJustRevealed(false));
      }
      setLoading(false);
      setSlowLoading(false);
    };
    if (elapsed < MIN_VISIBLE_MS) {
      setTimeout(finalize, MIN_VISIBLE_MS - elapsed);
    } else {
      finalize();
    }
    return success;
  };

  // Escalate the loading copy once the wait exceeds 5 s — keeps the existing
  // skeleton on screen but reassures the therapist nothing's stuck.
  useEffect(() => {
    if (!loading) { setSlowLoading(false); return undefined; }
    const t = setTimeout(() => setSlowLoading(true), 5000);
    return () => clearTimeout(t);
  }, [loading]);

  // Iter AO — auto-trigger on first entry to the Recommendations phase when a
  // verbal debrief is already on file. Single source of truth: the therapist
  // only describes the session once (on tx-notes). Auto-failure surfaces the
  // manual "Generate recommendations" fallback button below.
  useEffect(() => {
    if (!autoTrigger) return;
    if (autoFiredRef.current) return;
    if (!sessionId) return;
    if (r) return;                 // already have a bundle
    if (loading) return;
    if (!hasFindings) return;      // no source-of-truth input yet
    autoFiredRef.current = true;
    generate(false, { silent: true });
  }, [autoTrigger, sessionId, !!r, loading, hasFindings]);

  // Auto-loading skeleton (no recommendations yet, but auto-gen is in flight).
  // Iter AP — uses an inline layout-matching skeleton so the transition into
  // the real bundle is a swap of equivalent shapes, not a centered spinner
  // → wall-of-content jump.
  if (!r && loading) {
    return (
      <div
        className="bg-gradient-to-br from-amber-50/60 via-paper-50 to-sage-50/40 border border-paper-200 rounded-3xl p-6 sm:p-8"
        data-testid="recs-auto-loading"
        aria-busy="true"
        aria-live="polite"
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-9 h-9 rounded-full bg-amber-200/60 animate-pulse" />
          <div>
            <h2 className="font-heading text-xl text-ink-900 leading-tight">
              {slowLoading ? "Still drafting — almost there\u2026" : "Drafting your recommendations\u2026"}
            </h2>
            <p className="text-xs text-ink-500 mt-0.5" data-testid="recs-auto-loading-sub">
              {slowLoading
                ? "Claude is taking a moment with this client\u2019s history. Don\u2019t close the page."
                : "Using today\u2019s debrief, clinical findings & history. Usually under 15 s."}
            </p>
          </div>
        </div>

        {/* Skeleton mirroring the bundle layout (clinical_summary, home_care, stretches, client message, portal, qr). */}
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4 motion-safe:animate-pulse" aria-hidden="true">
          {/* clinical_summary block — spans full width on lg */}
          <div className="lg:col-span-2 rounded-2xl border border-paper-200 bg-white/70 p-5">
            <div className="h-3 w-32 bg-paper-200 rounded mb-3" />
            <div className="space-y-2">
              <div className="h-2.5 w-full bg-paper-100 rounded" />
              <div className="h-2.5 w-[92%] bg-paper-100 rounded" />
              <div className="h-2.5 w-[88%] bg-paper-100 rounded" />
              <div className="h-2.5 w-[70%] bg-paper-100 rounded" />
            </div>
          </div>
          {/* home_care */}
          <div className="rounded-2xl border border-paper-200 bg-white/70 p-5">
            <div className="h-3 w-24 bg-paper-200 rounded mb-3" />
            <div className="space-y-2">
              <div className="h-2.5 w-[90%] bg-paper-100 rounded" />
              <div className="h-2.5 w-[78%] bg-paper-100 rounded" />
              <div className="h-2.5 w-[84%] bg-paper-100 rounded" />
            </div>
          </div>
          {/* stretches */}
          <div className="rounded-2xl border border-paper-200 bg-white/70 p-5">
            <div className="h-3 w-28 bg-paper-200 rounded mb-3" />
            <div className="space-y-2">
              <div className="h-2.5 w-[88%] bg-paper-100 rounded" />
              <div className="h-2.5 w-[72%] bg-paper-100 rounded" />
            </div>
          </div>
          {/* client message */}
          <div className="rounded-2xl border border-paper-200 bg-white/70 p-5">
            <div className="h-3 w-20 bg-paper-200 rounded mb-3" />
            <div className="space-y-2">
              <div className="h-2.5 w-[94%] bg-paper-100 rounded" />
              <div className="h-2.5 w-[80%] bg-paper-100 rounded" />
            </div>
          </div>
          {/* portal */}
          <div className="rounded-2xl border border-paper-200 bg-white/70 p-5">
            <div className="h-3 w-24 bg-paper-200 rounded mb-3" />
            <div className="space-y-2">
              <div className="h-2.5 w-[90%] bg-paper-100 rounded" />
              <div className="h-2.5 w-[86%] bg-paper-100 rounded" />
              <div className="h-2.5 w-[60%] bg-paper-100 rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Unprimed state — only reached when auto-gen has not fired OR has failed.
  // Shown as a quiet fallback: the headline + helper text adapts.
  if (!r) {
    const failureMode = autoFailed;
    return (
      <div className="bg-gradient-to-br from-amber-50/60 via-paper-50 to-sage-50/40 border border-paper-200 rounded-3xl p-6 sm:p-8 text-center" data-testid="recs-unprimed">
        <Sparkles className="w-8 h-8 text-amber-600 mx-auto mb-3" />
        <h2 className="font-heading text-2xl text-ink-900 mb-2">
          {failureMode ? "Recommendations didn\u2019t generate" : "Generate AI recommendations"}
        </h2>
        <p className="text-ink-700 max-w-xl mx-auto mb-6">
          {failureMode
            ? "Auto-generation hit a snag. Tap below to retry — your debrief and findings are still saved."
            : "Once you save a verbal debrief on the previous step, this will draft itself. You can also start it manually."}
        </p>
        <button
          type="button"
          onClick={() => generate(false)}
          disabled={loading || !hasFindings}
          data-testid="generate-recs-btn"
          className="min-h-[48px] px-7 rounded-full bg-ink-900 text-white font-medium hover:bg-ink-800 disabled:opacity-50 inline-flex items-center gap-2"
        >
          <Sparkles className="w-4 h-4" />
          {loading ? "Generating\u2026" : "Generate recommendations"}
        </button>
        {!hasFindings && (
          <p className="text-xs text-amber-700 mt-3" data-testid="recs-prereq-warning">
            Add a verbal debrief on the previous step first \u2014 it grounds the AI&rsquo;s tone and priority.
          </p>
        )}
      </div>
    );
  }

  // Primed state. Iter AP — fade-in on first reveal (skeleton → content).
  return (
    <div
      className={`space-y-5 motion-safe:transition-opacity motion-safe:duration-200 ${justRevealed ? "opacity-0" : "opacity-100"}`}
      data-testid="recs-primed"
    >
      {stale && (
        <div className="border border-amber-300 bg-amber-50 rounded-3xl p-4 flex items-start justify-between gap-3 flex-wrap" data-testid="recs-stale-banner">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
            <div>
              <div className="font-medium text-ink-900">New outcome data available</div>
              <div className="text-sm text-ink-700">Pain or ROM was updated after the recommendations were generated. Re-run to incorporate the new numbers.</div>
            </div>
          </div>
          <button
            type="button"
            onClick={() => generate(true)}
            disabled={loading}
            data-testid="refresh-recs-btn"
            className="min-h-[40px] px-4 rounded-full bg-amber-500 text-white text-sm font-medium hover:bg-amber-600 disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh recommendations
          </button>
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3" data-testid="recs-header">
        <div className="text-xs text-ink-500">
          Generated {r.generated_at ? new Date(r.generated_at).toLocaleString() : ""}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => setSendModalOpen(true)}
            data-testid="open-send-to-client-btn"
            className="text-sm font-medium min-h-[40px] px-5 rounded-full bg-sage-500 hover:bg-sage-600 text-white inline-flex items-center gap-1.5"
          >
            <Send className="w-4 h-4" />
            Review &amp; send to client
          </button>
          <button
            type="button"
            onClick={() => generate(true)}
            disabled={loading}
            data-testid="regenerate-recs-btn"
            className="text-xs text-ink-700 hover:text-ink-900 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full hover:bg-paper-100 disabled:opacity-50"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
            Regenerate
          </button>
        </div>
      </div>

      {r.clinical_summary && (
        <SectionCard title="Clinical summary (SOAP-style)" testid="recs-clinical-summary">
          <div className="flex justify-end mb-1"><CopyButton text={r.clinical_summary} testid="copy-clinical-summary" /></div>
          <p className="text-sm text-ink-900 whitespace-pre-line leading-relaxed">{r.clinical_summary}</p>
        </SectionCard>
      )}

      {r.todays_session && (
        <SectionCard title="Today's session (what the client sees first)" testid="recs-todays-session">
          <div className="flex justify-end mb-1"><CopyButton text={r.todays_session} testid="copy-todays-session" /></div>
          <p className="text-sm text-ink-900 whitespace-pre-line leading-relaxed">{r.todays_session}</p>
          {r.biggest_win && (
            <div className="mt-3 bg-sage-500/10 text-sage-700 rounded-2xl px-4 py-2.5 text-sm font-medium" data-testid="recs-biggest-win">
              {r.biggest_win}
            </div>
          )}
        </SectionCard>
      )}

      {!!(r.home_care || []).length && (
        <SectionCard title="Home care recommendations" testid="recs-home-care">
          <ul className="space-y-2">
            {r.home_care.map((item, i) => (
              <li key={i} className="text-sm text-ink-900 flex items-start gap-2" data-testid={`home-care-item-${i}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-sage-500 mt-2 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {!!(r.stretches || []).length ? (
        <SectionCard title="Stretches & exercises" testid="recs-stretches">
          <ul className="space-y-3">
            {r.stretches.map((s, i) => (
              <li key={i} data-testid={`stretch-item-${i}`}>
                <div className="text-sm font-medium text-ink-900">
                  {s.name}
                  {s.dosage && <span className="text-ink-500 font-normal"> — {s.dosage}</span>}
                </div>
                {s.how_to && <div className="text-xs text-ink-600 mt-0.5 leading-relaxed">{s.how_to}</div>}
                {s.targets && <div className="text-[11px] text-sage-700 mt-0.5" data-testid={`stretch-targets-${i}`}>Addresses: {s.targets}</div>}
              </li>
            ))}
          </ul>
        </SectionCard>
      ) : !!(r.suggested_stretches || []).length && (
        <SectionCard title="Suggested stretches" testid="recs-stretches">
          <ul className="space-y-2">
            {r.suggested_stretches.map((item, i) => (
              <li key={i} className="text-sm text-ink-900 flex items-start gap-2" data-testid={`stretch-item-${i}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mt-2 shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </SectionCard>
      )}

      {r.follow_up?.ideal?.when && (
        <SectionCard title="Recommended follow-up" testid="recs-follow-up">
          <div className="text-sm text-ink-900">
            <span className="font-medium">Ideal: return {r.follow_up.ideal.when}.</span>
            {r.follow_up.ideal.why && <span className="text-ink-700"> {r.follow_up.ideal.why}</span>}
          </div>
          {r.follow_up.alternative?.when && (
            <div className="text-xs text-ink-500 mt-2" data-testid="recs-follow-up-alt">
              If that's not possible: {r.follow_up.alternative.when}{r.follow_up.alternative.why ? ` — ${r.follow_up.alternative.why}` : ""}
            </div>
          )}
        </SectionCard>
      )}

      {r.next_visit && (
        <SectionCard title="Next visit focus" testid="recs-next-visit">
          <p className="text-sm text-ink-900 leading-relaxed">{r.next_visit}</p>
        </SectionCard>
      )}

      {r.client_message && (
        <SectionCard title="Client message (text/SMS-ready)" testid="recs-client-message">
          <div className="flex justify-end mb-1"><CopyButton text={r.client_message} testid="copy-client-message" /></div>
          <p className="text-sm text-ink-900 whitespace-pre-line leading-relaxed">{r.client_message}</p>
        </SectionCard>
      )}

      {r.progress_portal_content && (
        <SectionCard title="Progress portal content" testid="recs-portal-content">
          <div className="flex justify-end mb-1"><CopyButton text={r.progress_portal_content} testid="copy-portal-content" /></div>
          <p className="text-sm text-ink-900 whitespace-pre-line leading-relaxed">{r.progress_portal_content}</p>
        </SectionCard>
      )}

      {r.qr_share_content && (
        <SectionCard title="QR share card content" testid="recs-qr-content">
          <div className="flex justify-end mb-1"><CopyButton text={r.qr_share_content} testid="copy-qr-content" /></div>
          <p className="text-sm text-ink-900 whitespace-pre-line leading-relaxed">{r.qr_share_content}</p>
        </SectionCard>
      )}

      <SendToClientModal
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        sessionId={sessionId}
        bundle={r}
        client={client}
      />
    </div>
  );
}
